import React, { useEffect, useRef } from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { SmartAnswerFooterSection } from '../../ai-answer-dialog/ai-answer-footer/ai-answer-footer';
import {
	onAIAnswerResultCompleteShown,
	getSearchAIAnalyticsContextData,
	SearchAIAnalyticsContextLocation,
} from '../../ai-answer-dialog/analytics';
import { SearchAIAnalyticsContext } from '../../ai-answer-dialog/search-ai-analytics-context';
import { useSAINUsageCriteriaMet } from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { AISourcesCarousel } from '../../common/ui/ai-sources';
import { useKnowledgeSmartCardAnalytics } from '../../common/ui/knowledge-smart-card/hooks/use-knowledge-smart-card-analytics';
import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import type { QueryIntent } from '../../constants';
import { useContentTruncationDetector } from '../common/hooks/use-content-truncation-detector';
import { AICollapse } from '../common/ui/ai-collapse';
import { AIDisclaimerAndFeedback } from '../common/ui/ai-disclaimer-and-feedback';
import { ReactRenderer } from '../common/ui/react-renderer';
import { ReadMoreButton } from '../common/ui/read-more-button';

import { messages } from './messages';

const styles = cssMap({
	fullHeight: {
		height: '100%',
	},
	sectionContainer: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.050'),
	},
	sourcesTopPadding: {
		paddingTop: token('space.050'),
	},
	nonInteractive: {
		pointerEvents: 'none',
	},
});

export function SmartAnswerCardBodyComplete({
	collapsed,
	onExpandCollapseButtonClick,
	queryIntent,
}: {
	collapsed: boolean;
	onExpandCollapseButtonClick: () => void;
	queryIntent?: QueryIntent;
}): React.JSX.Element {
	const context = useAIAnswerContext();
	const { commonAttributes } = context;
	const { answerString, sources, errorStateSuggestedQueries, searchSessionId } = commonAttributes;
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const [fireAnalyticsEvent] = useAnalytics();

	const renderedContentRef = useRef<HTMLDivElement>(null);
	const contentContainerRef = useRef<HTMLDivElement>(null);
	const completeAnswerAnalyticsEventFiredRef = useRef(false);

	// Detect if content is truncated when collapsed
	const isContentTruncated = useContentTruncationDetector(
		renderedContentRef,
		contentContainerRef,
		collapsed,
		[answerString],
	);

	useEffect(() => {
		// Only fire once and ensure DOM element is ready with meaningful content
		if (completeAnswerAnalyticsEventFiredRef.current || !renderedContentRef.current) {
			return;
		}
		const answerHeight = renderedContentRef.current.getBoundingClientRect().height;
		const containerHeight = contentContainerRef.current?.getBoundingClientRect().height ?? 0;
		const whitespaceHeight = Math.max(containerHeight - answerHeight, 0);

		// Only fire if we have meaningful height (content is rendered)
		if (answerHeight > 0 && containerHeight > 0) {
			fireAnalyticsEvent(
				onAIAnswerResultCompleteShown({
					...commonAttributes,
					answerHeight,
					containerHeight,
					whitespaceHeight,
					queryIntent: queryIntent,
				}),
			);
			completeAnswerAnalyticsEventFiredRef.current = true;
		}
	}, [fireAnalyticsEvent, commonAttributes, queryIntent, isContentTruncated]);

	const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
		abTestId: 'kd_sain_show_no_answer_suggestions_exp',
	});

	const showReadMoreButton = collapsed && isContentTruncated;

	useEffect(() => {
		fireSAINUsageEvent({ commonAttributes });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { handleSmartCardClick } = useKnowledgeSmartCardAnalytics({
		aiAnswerDialogContext: context,
	});

	return (
		<Stack
			alignBlock="stretch"
			grow="fill"
			spread="space-between"
			testId="smart-answer-card-body-complete"
			xcss={styles.fullHeight}
		>
			<Stack alignBlock="stretch" grow="fill" spread="space-between" xcss={styles.fullHeight}>
				<Box>
					<AICollapse
						title={
							<Heading as="h2" size="xxsmall">
								<Text color="color.text.subtlest" size="small" weight="bold">
									<FormattedMessage {...messages.smart_answer_card_title} />
								</Text>
							</Heading>
						}
						isCollapsed={collapsed}
						onExpandCollapse={onExpandCollapseButtonClick}
						ref={contentContainerRef}
						showReadMoreButton={showReadMoreButton}
					>
						<SearchAIAnalyticsContext
							data={getSearchAIAnalyticsContextData({
								location: SearchAIAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
								commonAttributes,
							})}
						>
							<SmartCardProvider>
								<Box id="ai-answer-result-section" ref={renderedContentRef}>
									<ReactRenderer markdown={answerString} />
								</Box>
							</SmartCardProvider>
						</SearchAIAnalyticsContext>
						{sources.length > 0 && SAINFeatureControl.isSourcesInShortSAINAnswersEnabled() ? (
							<Box xcss={cx(styles.sourcesTopPadding, collapsed && styles.nonInteractive)}>
								<AISourcesCarousel
									sources={sources}
									searchSessionId={searchSessionId}
									onClickHandler={handleSmartCardClick}
								/>
							</Box>
						) : null}
					</AICollapse>
					{showReadMoreButton && !SAINFeatureControl.isSAINUXUpliftEnabled() && (
						<ReadMoreButton onClick={onExpandCollapseButtonClick} />
					)}
				</Box>
				{!collapsed &&
				sources.length > 0 &&
				!SAINFeatureControl.isSourcesInShortSAINAnswersEnabled() ? (
					<Box xcss={styles.sectionContainer}>
						<AISourcesCarousel
							sources={sources}
							searchSessionId={searchSessionId}
							onClickHandler={handleSmartCardClick}
						/>
					</Box>
				) : null}
				<Box xcss={styles.sectionContainer}>
					<AIDisclaimerAndFeedback
						showFeedbackButtons={true}
						showCopyButton
						renderedContentRef={renderedContentRef}
					/>
				</Box>
			</Stack>
			{!collapsed ||
			(errorStateSuggestedQueries.length > 0 && serveErrorStateSuggestedQueriesExp) ? (
				<SmartAnswerFooterSection showOpenChat={true} />
			) : null}
		</Stack>
	);
}
